// SecurePaymentLoader.jsx
import React from "react";
import { motion } from "framer-motion";
import PublishLoader from "./PublishLoader";

const PAYMENT_MESSAGES = [
  "🔒 Initiating secure payment...",
  "🛡️ Connecting to payment gateway...",
  "✅ Verifying transaction details...",
  "💳 Processing your payment...",
];

const SecurePaymentAnimation = () => {
  // Shield/Lock animation variants
  const shieldVariants = {
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const lockVariants = {
    animate: {
      y: [0, -3, 0],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const checkmarkVariants = {
    initial: { pathLength: 0, opacity: 0 },
    animate: {
      pathLength: [0, 1, 1, 0],
      opacity: [0, 1, 1, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
        times: [0, 0.3, 0.7, 1],
      },
    },
  };

  const pulseRingVariants = {
    animate: (custom) => ({
      scale: [1, 1.4, 1.4],
      opacity: [0.6, 0.3, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeOut",
        delay: custom * 0.4,
      },
    }),
  };

  const particleVariants = {
    animate: (custom) => ({
      y: [0, -30, -30],
      x: [0, custom % 2 === 0 ? 15 : -15, custom % 2 === 0 ? 20 : -20],
      opacity: [0, 0.8, 0],
      scale: [0.5, 1, 0.5],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeOut",
        delay: custom * 0.3,
      },
    }),
  };

  const spinnerVariants = {
    animate: {
      rotate: 360,
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "linear",
      },
    },
  };

  return (
    <motion.svg
      width="200"
      height="200"
      viewBox="0 0 200 200"
      className="main-svg"
    >
      {/* Pulse rings for trust effect */}
      {[0, 1, 2].map((i) => (
        <motion.circle
          key={`pulse-${i}`}
          cx="100"
          cy="100"
          r="60"
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          opacity="0"
          custom={i}
          variants={pulseRingVariants}
          animate="animate"
        />
      ))}

      {/* Rotating security border */}
      <motion.circle
        cx="100"
        cy="100"
        r="70"
        fill="none"
        stroke="url(#secureGradient)"
        strokeWidth="3"
        strokeDasharray="10,5"
        variants={spinnerVariants}
        animate="animate"
      />

      {/* Security particles */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.circle
          key={`particle-${i}`}
          cx={100 + 50 * Math.cos((i * Math.PI) / 3)}
          cy={100 + 50 * Math.sin((i * Math.PI) / 3)}
          r="3"
          fill="#10b981"
          custom={i}
          variants={particleVariants}
          animate="animate"
        />
      ))}

      {/* Main Shield Shape */}
      <motion.g variants={shieldVariants} animate="animate">
        {/* Shield body */}
        <path
          d="M 100 50 
             C 85 50, 70 55, 70 65
             L 70 95
             C 70 115, 85 125, 100 135
             C 115 125, 130 115, 130 95
             L 130 65
             C 130 55, 115 50, 100 50 Z"
          fill="url(#shieldGradient)"
          stroke="#059669"
          strokeWidth="2"
        />

        {/* Shield highlight */}
        <path
          d="M 100 52 
             C 88 52, 75 56, 75 65
             L 75 75
             C 75 70, 85 65, 100 65
             C 115 65, 125 70, 125 75
             L 125 65
             C 125 56, 112 52, 100 52 Z"
          fill="rgba(255, 255, 255, 0.15)"
        />

        {/* Lock body */}
        <motion.g variants={lockVariants} animate="animate">
          {/* Lock base */}
          <rect x="90" y="85" width="20" height="25" rx="2" fill="#047857" />

          {/* Keyhole */}
          <circle cx="100" cy="92" r="3" fill="#d1fae5" />
          <rect x="98" y="92" width="4" height="8" rx="1" fill="#d1fae5" />

          {/* Lock shackle */}
          <path
            d="M 92 85 
               L 92 75 
               A 8 8 0 0 1 108 75
               L 108 85"
            fill="none"
            stroke="#047857"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </motion.g>
      </motion.g>

      {/* Security badge stars */}
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.polygon
          key={`star-${i}`}
          points={`${100 + 85 * Math.cos((i * 2 * Math.PI) / 5)},${
            100 + 85 * Math.sin((i * 2 * Math.PI) / 5)
          } ${100 + 88 * Math.cos((i * 2 * Math.PI) / 5 + 0.2)},${
            100 + 88 * Math.sin((i * 2 * Math.PI) / 5 + 0.2)
          } ${100 + 88 * Math.cos((i * 2 * Math.PI) / 5 - 0.2)},${
            100 + 88 * Math.sin((i * 2 * Math.PI) / 5 - 0.2)
          }`}
          fill="#10b981"
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, 1, 1, 0],
            scale: [0, 1, 1, 0],
            transition: {
              duration: 3,
              repeat: Infinity,
              delay: i * 0.2,
              ease: "easeInOut",
            },
          }}
        />
      ))}

      {/* Gradients */}
      <defs>
        <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
        <linearGradient id="secureGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10b981" />
          <stop offset="50%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
};

// Export ready-to-use payment loader
const PaymentLoader = ({ isOpen, onClose, minDuration = 2000 }) => {
  return (
    <PublishLoader
      isOpen={isOpen}
      onClose={onClose}
      minDuration={minDuration}
      messages={PAYMENT_MESSAGES}
      cycleInterval={2000}
    >
      <SecurePaymentAnimation />
    </PublishLoader>
  );
};

export default PaymentLoader;
